import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from '@fs/zion-axios'
import { deletePlaylist as deletePlaylistApi } from '../../api'
import notifySentry from '../../lib/helpers/sentryNotifier'
import type { Playlist } from '../../api/rootstech/playlist/playlist.types'

type PlaylistsCache = { data: { results: Playlist[] } }

type UsePlaylistDeletion = Omit<UseMutationResult<void, AxiosError, string>, 'mutateAsync'> & {
  deletePlaylist: (playlistId: string) => Promise<void>
}

export const usePlaylistDeletion = (): UsePlaylistDeletion => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  const { mutateAsync: deletePlaylist, ...rest } = useMutation<void, AxiosError, string>({
    mutationFn: (playlistId: string) => deletePlaylistApi({ playlistId }),
    onSuccess: (_, playlistId) => {
      queryClient.setQueryData<PlaylistsCache>(['playlists'], (old) => {
        if (!old) return old
        return {
          ...old,
          data: {
            ...old.data,
            results: old.data.results.filter((p) => p.id !== playlistId),
          },
        }
      })
      showStatusOverlay({
        message: t('playlists.alerts.playlist-deleted', 'Playlist deleted.'),
        type: 'success',
        transient: true,
      })
    },
    onError: (err: AxiosError) => {
      notifySentry({ ...err.response, err })
      showStatusOverlay({
        message: t(
          'playlists.errors.playlist.delete-error',
          "We're sorry, but there was an issue deleting the playlist. Please try again shortly."
        ),
        type: 'error',
        dismissable: true,
        transient: true,
      })
    },
  })

  return { deletePlaylist, ...rest }
}
